import styled from 'styled-components';
import { preserveCatalogVehicleImageAspectRatio } from 'pages/CatalogPage/common/CatalogHeader/CatalogHeader.styled';
import { Flex } from 'UI';

export const CATALOG_HEADER_IMAGE_WIDTH = 50;
export const CATALOG_HEADER_IMAGE_HEIGHT = 40;

export const CatalogHeaderWrapper = styled(Flex)<{ $connected?: boolean }>`
  width: 100%;
  flex-direction: column;
  background: ${({ theme }) => theme.color.white};
  border: ${({ $connected, theme }) => ($connected ? 'none' : `1px solid ${theme.color.grey85}`)};
  border-radius: ${({ $connected }) => ($connected ? '0' : '12px')};
  padding: 1px 8px;
  gap: 0;
  box-sizing: border-box;
  min-height: ${CATALOG_HEADER_IMAGE_HEIGHT + 2}px;
  justify-content: center;

  /* Tablet optimization: more aggressive reduction */
  @media (max-width: 1023px) {
    padding: 0px 3px;
    min-height: 38px;
  }

  /* Mobile optimization: match tablet sizing */
  @media (max-width: 765px) {
    padding: 0px 3px;
    min-height: 38px;
  }
`;

export const CatalogHeaderTopGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: ${CATALOG_HEADER_IMAGE_WIDTH}px minmax(0, 1fr) auto;
  grid-template-areas: 'image content actions';
  column-gap: 6px;
  align-items: center;
  height: ${CATALOG_HEADER_IMAGE_HEIGHT}px;

  /* Tablet optimization */
  @media (max-width: 1023px) {
    column-gap: 4px;
    height: 36px;
  }

  /* Mobile optimization: match tablet sizing */
  @media (max-width: 765px) {
    column-gap: 4px;
    height: 36px;
  }
`;

export const CatalogHeaderImageSlot = styled.div`
  grid-area: image;
  width: ${CATALOG_HEADER_IMAGE_WIDTH}px;
  height: ${CATALOG_HEADER_IMAGE_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  flex-shrink: 0;
  overflow: visible;

  & > div {
    width: 100%;
    height: 100%;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
  }

  /* Tablet optimization: reduce image from 50x40 to 38x30 */
  @media (max-width: 1023px) {
    width: 38px;
    height: 30px;
  }

  /* Mobile optimization: match tablet sizing (38x30) */
  @media (max-width: 765px) {
    width: 38px;
    height: 30px;
  }

  ${preserveCatalogVehicleImageAspectRatio}
`;

export const CatalogHeaderContent = styled(Flex)`
  grid-area: content;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
  min-width: 0;
  height: 100%;
  flex-wrap: nowrap;
  overflow: hidden;

  /* Tablet optimization */
  @media (max-width: 1023px) {
    gap: 6px;
  }

  /* Mobile optimization: match tablet sizing */
  @media (max-width: 765px) {
    gap: 6px;
  }
`;

export const CatalogHeaderIdentityLeft = styled(Flex)`
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  min-width: 0;
  flex-shrink: 1;
  overflow: hidden;
  white-space: nowrap;
  height: 100%;

  /* Tablet optimization: reduce text size */
  @media (max-width: 1023px) {
    gap: 2px;
    font-size: 0.8rem;
  }

  /* Mobile optimization: match tablet sizing */
  @media (max-width: 765px) {
    gap: 2px;
    font-size: 0.8rem;
  }
`;

export const CatalogHeaderIdentityItem = styled(Flex)`
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
  height: 100%;

  /* Tablet optimization: reduce text size */
  @media (max-width: 1023px) {
    gap: 2px;
    font-size: 0.8rem;
  }

  /* Mobile optimization: match tablet sizing */
  @media (max-width: 765px) {
    gap: 2px;
    font-size: 0.8rem;
  }
`;

export const CatalogHeaderDivider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid ${({ theme }) => theme.color.grey90};
  margin: 0;
`;

export const CatalogExpandedSection = styled.div`
  width: 100%;
  padding: 10px 8px 0 8px;
  box-sizing: border-box;

  /* Tablet optimization: reduce padding */
  @media (max-width: 1023px) {
    padding: 10px 3px 0 3px;
  }

  /* Mobile optimization: match tablet padding */
  @media (max-width: 765px) {
    padding: 10px 3px 0 3px;
  }
`;

export const CatalogHeaderActions = styled(Flex)`
  grid-area: actions;
  grid-row: auto;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  justify-self: end;
  align-self: center;
  flex-shrink: 0;

  [data-cy='ots-icon'] {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: center;
  }
`;

export const CatalogHeaderDesktopOptions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const CatalogIconActionButton = styled.button`
  border: none;
  background: ${({ theme }) => theme.color.grey95};
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.color.grey75};
  }

  /* Tablet optimization: reduce button size from 28px to 20px */
  @media (max-width: 1023px) {
    width: 20px;
    height: 20px;
  }

  /* Mobile optimization: reduce button size from 28px to 17px */
  @media (max-width: 765px) {
    width: 17px;
    height: 17px;
  }
`;
